import Layers from "slotworks/graphics/layers/layers";
import Contract from "slotworks/utils/contract";
import * as TweenJS from "slotworks/utils/tween";
import Rectangle from "slotworks/utils/geom/rectangle";
import Logger = require("js-logger");
import currency from "slotworks/i18n/currency";
import soundManager from "slotworks/sound/sound-manager";
import { sequence, parallel } from "slotworks/utils/race-utils";
import ICFreespinRecord from "integration/revolver/schema/freespin-record";
import gameState from "slotworks/model/game-state";
import { CenterPivot } from "slotworks/graphics/pixi/group";
import canvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import Point from "slotworks/utils/geom/point";
import deviceInfo from "slotworks/utils/device-info";
import { StartBaseMusic } from "commands/music";

export default class CoinpotsTexts {

    public static readonly IDS = [1, 2, 4, 5];

    public static MULTI_FONT_SCALE = 1.2;

    private potTexts: PotText[];

    private count: number;

    private multiCombined: PIXI.extras.BitmapText;

    public constructor() {
        this.potTexts = [];
    }

    public init() {
        // glows behind pots
        Layers.Custom.get("CoinCollectPotsGlows").moveBehind(Layers.Custom.get("CoinCollectPots"));
        for (let id of CoinpotsTexts.IDS) {
            this.potTexts[id] = new PotText(id);
        }
        this.multiCombined = Layers.Custom.get("CoinCollectPotsForeground").getBitmapText("combined");
        this.multiCombined.visible = false;
        this.count = 0;
    }

    public showText(id: number, amount: number, sign: boolean = false, showGlow: boolean = false): Contract<void> {
        if (id === 1 || id === 5)
            this.count++;
        if (this.potTexts[id])
            return this.potTexts[id].showText(amount, sign, this.count, showGlow);
        else
            return Contract.empty();
    }

    public hideTexts() {
        const funcs = [];
        for (let potText of this.potTexts) {
            if (potText)
                potText.slowHide().execute();
        }
        this.multiCombined.visible = false;
        this.count = 0;
    }

    public finish(): Contract<void> {
        if (this.count >= 1) {
            if (this.potTexts[1].isVisible() && this.potTexts[5].isVisible()) {
                return sequence([
                    () => Contract.getTimeoutContract(200),
                    () => Contract.wrap(() => {
                        soundManager.customEvent("multiplier_award_10x");
                        // reposition in case of orientation change
                        this.multiCombined = Layers.Custom.get("CoinCollectPotsForeground").getBitmapText("combined");
                    }),
                    () => parallel([
                        () => this.potTexts[1].combineText(this.multiCombined),
                        () => this.potTexts[5].combineText(this.multiCombined),
                    ]),
                    () => Layers.CelebrationBackground.setScene("glowstack"),
                    () => Layers.CelebrationContent.setScene("potWin"),
                    () => new Contract(resolve => {
                        const valueText = Layers.CelebrationContent.getBitmapText("value");
                        valueText.text = currency.format(this.potTexts[1].getAmount() + this.potTexts[5].getAmount(), undefined, undefined, true);
                        valueText.visible = true;
                        this.potTexts[1].hideText();
                        this.potTexts[5].hideText();
                        new TweenJS.Tween(valueText.scale)
                            .to({ x: [0.9, 1], y: [0.9, 1] }, 200)
                            .onComplete(() => resolve(null))
                            .start();
                    }),
                    () => Contract.getTimeoutContract(2000),
                    () => Contract.wrap(() => soundManager.customEvent("lines_win_out")),
                    () => new Contract(resolve => {
                        const valueText = Layers.CelebrationContent.getBitmapText("value");
                        new TweenJS.Tween(valueText.scale)
                            .to({ x: 0, y: 0 }, 200)
                            .onComplete(() => resolve(null))
                            .start();
                    }),
                    () => Contract.getTimeoutContract(200),
                    () => Layers.CelebrationContent.defaultScene(),
                    () => Layers.CelebrationBackground.defaultScene(),
                    () => Contract.wrap(() => {
                        // restore music if base game
                        if (!(gameState.getCurrentGame().getCurrentRecord() instanceof ICFreespinRecord)) {
                            // soundManager.customEvent("base_reels_background_start");
                            StartBaseMusic(true);
                        }
                        this.count = 0;
                    })
                ]);
            }
        }
        return Contract.empty();
    }

}

// keeps its own state
class PotText {
    private text: PIXI.extras.BitmapText;
    private amount: number;
    private sign: string = "";
    private startPosition: Rectangle;
    private endPosition: Rectangle;
    private isFreespinsText: boolean = false;
    private id: number;
    private glowStack: PIXI.Container;
    private tweens: TweenJS.Tween[];
    private glowSprites: PIXI.Sprite[];
    private glowToMiddle: boolean = false;

    constructor(id: number) {
        this.id = id;
        // todo refactor
        if (id === 2 || id === 4) {
            this.isFreespinsText = true;
            this.sign = '+';
        }
        if (id === 1 || id === 5) {
            // create glow stack
            const glowStrings = [`pot${id}_glow_5`, `pot${id}_glow_4`, `pot${id}_glow_3`, `pot${id}_glow_2`, `pot${id}_glow_1`];
            this.glowSprites = [];
            for (let s of glowStrings)
                this.glowSprites.push(Layers.Custom.get("CoinCollectPotsGlows").getSprite(s));

            this.glowStack = CenterPivot(this.glowSprites);
            this.glowStack.visible = false;
        }
        this.text = Layers.Custom.get("CoinCollectPotsForeground").getBitmapText(`pot${id}value`);
        this.text.visible = false;
        this.amount = 0;

        this.startPosition = Layers.Custom.get("CoinCollectPotsForeground").getPosition(`pot${id}_start`);
        this.endPosition = Layers.Custom.get("CoinCollectPotsForeground").getPosition(`pot${id}_end`);
    }

    public isVisible() {
        return this.text.visible;
    }

    public hideText() {
        this.text.visible = false;
        this.amount = 0;
        this.hideGlowElements();
    }

    public slowHide(): Contract<void> {
        this.amount = 0;
        if (this.text.visible)
            return new Contract(resolve => {
                if (this.id === 1 || this.id === 5) {
                    soundManager.customEvent("lines_win_out");
                }
                new TweenJS.Tween(this.text.scale)
                    .to({ x: 0, y: 0 }, 200)
                    .onComplete(() => { this.text.visible = false; resolve(null); })
                    .start();
                this.hideGlowElements();
            });
        else
            return Contract.empty();
    }

    public getAmount() {
        return this.amount;
    }

    public combineText(target: PIXI.extras.BitmapText) {
        return new Contract(resolve => {
            let x = target.x;
            let y = target.y;
            if (this.glowToMiddle) {
                if (this.id === 1) {
                    x -= this.text.width / 2;
                }
                if (this.id === 5) {
                    x += this.text.width / 2;
                }
            }
            new TweenJS.Tween(this.text)
                .to({ x: x, y: y }, 350)
                .onUpdate(() => {
                    let tx = this.text.x;
                    let ty = this.text.y;
                    if (this.glowToMiddle) {
                        if (this.id === 1) {
                            tx += this.text.width / 2;
                        }
                        if (this.id === 5) {
                            tx -= this.text.width / 2;
                        }
                    }
                    const position = Layers.Custom.get("CoinCollectPotsForeground").localToLocal(Layers.Custom.get("CoinCollectPotsGlows"), new Rectangle(tx, ty));
                    this.glowStack.position.set(position.x, position.y);

                })
                .onComplete(() => { resolve(null); })
                .start();
        });
    }

    private isMulti5xText() {
        return this.id === 1 || this.id === 5;
    }

    private showGlowElements() {
        if (!this.isMulti5xText())
            return;
        let position
        if (this.glowToMiddle) {
            let x = this.id == 1 ? this.text.x + this.text.width / 2 : this.text.x - this.text.width / 2;
            position = Layers.Custom.get("CoinCollectPotsForeground").localToLocal(Layers.Custom.get("CoinCollectPotsGlows"), new Rectangle(x, this.text.y));
        } else {
            position = Layers.Custom.get("CoinCollectPotsForeground").localToLocal(Layers.Custom.get("CoinCollectPotsGlows"), new Rectangle(this.text.x, this.text.y));
        }
        this.glowStack.visible = true;
        this.glowStack.position.set(position.x, position.y);
        this.glowStack.scale.set(0, 0);
        if (!this.tweens) {
            this.tweens = [];
            this.glowSprites.forEach(sprite => {
                sprite.anchor.set(0.5, 0.5);
                sprite.x += sprite.width / 2;
                sprite.y += sprite.height / 2;
            })
            this.tweens.push(new TweenJS.Tween(
                this.glowSprites[3])
                .to({ rotation: Math.PI * 2 }, 20000)
                .repeat(Infinity));

            this.tweens.push(new TweenJS.Tween(
                this.glowSprites[2])
                .to({ rotation: -Math.PI * 2 }, 30000)
                .repeat(Infinity));

            this.tweens.push(new TweenJS.Tween(
                this.glowSprites[1])
                .to({ rotation: Math.PI * 2 }, 60000)
                .repeat(Infinity));
        }
        this.glowStack.visible = true;
        new TweenJS.Tween(this.glowStack.scale)
            .to({ x: 1.1, y: 1.1 }, 200)
            .onComplete(() => this.tweens.forEach(tween => tween.start()))
            .start();
    }

    private hideGlowElements() {
        if (!this.isMulti5xText())
            return;
        this.tweens.forEach(tween => tween.stop());
        new TweenJS.Tween(this.glowStack.scale)
            .to({ x: 0, y: 0 }, 200)
            .onComplete(() => { this.glowStack.visible = false; })
            .start();
    }


    // todo tweens durations as constants, from config, etc
    public showText(amount: number, sign: boolean, instant_win_n: number = 1, showGlow: boolean = false) {
        if (this.text.visible) return new Contract<void>(resolve => {
            const obj = { value: this.amount };
            new TweenJS.Tween(obj)
                .to({ value: this.amount + amount }, 500)
                .onUpdate(() => {
                    if (this.isFreespinsText)
                        this.text.text = sign ? this.sign + Math.floor(obj.value) : Math.floor(obj.value).toString();
                    else
                        this.text.text = currency.format(obj.value, undefined, undefined, true, true);
                })
                .onComplete(() => {
                    this.amount += amount;
                    resolve(null);
                })
                .start();
            new TweenJS.Tween(this.text.scale)
                .to({ x: [0.8, 1], y: [0.8, 1] }, 200)
                .onComplete(() => {
                    if (!this.isFreespinsText)
                        soundManager.customEvent(`feature_pot_instant_win_${instant_win_n}`)
                }).start();
        });
        else return new Contract<void>(resolve => {
            if (!deviceInfo.isDesktop) {
                this.startPosition = Layers.Custom.get("CoinCollectPotsForeground").getPosition(`pot${this.id}_start`);
                this.endPosition = Layers.Custom.get("CoinCollectPotsForeground").getPosition(`pot${this.id}_end`);
                if (canvasManager.orientation == Orientation.PORTRAIT) {
                    if (this.id === 1) {
                        this.text.anchor = new PIXI.Point(0, 0.5);
                        const left = Layers.Custom.get("CoinCollectPotsForeground").globalToLocal(new Point(20, 0)).x;
                        //    this.startPosition.x = left;
                        this.endPosition.x = left;
                        this.glowToMiddle = true;
                    }
                    if (this.id === 5) {
                        this.text.anchor = new PIXI.Point(1, 0.5);
                        const right = Layers.Custom.get("CoinCollectPotsForeground").globalToLocal(new Point(canvasManager.stageWidth - canvasManager.stageOffsetX * 2, 0)).x;
                        this.endPosition.x = right;
                        this.glowToMiddle = true;
                    }
                } else {
                    if (this.id === 1 || this.id === 5) {
                        this.text.anchor = new PIXI.Point(0.5, 0.5);
                        this.glowToMiddle = false;
                    }
                }
            }
            this.text.visible = true;
            this.text.scale.set(0);
            this.text.position.set(this.startPosition.x, this.startPosition.y);
            this.amount = 0;
            const obj = { value: 0 };
            new TweenJS.Tween(obj)
                .to({ value: amount }, 500)
                .onUpdate(() => {
                    if (this.isFreespinsText)
                        this.text.text = sign ? this.sign + Math.floor(obj.value) : Math.floor(obj.value).toString();
                    else
                        this.text.text = currency.format(obj.value, undefined, undefined, true, true);
                })
                .onComplete(() => {
                    this.amount = amount;
                    resolve(null);
                }).start();
            new TweenJS.Tween(this.text.scale)
                .to({ x: 1, y: 1 }, 200)
                .onComplete(() => {
                    if (!this.isFreespinsText)
                        soundManager.customEvent(`feature_pot_instant_win_${instant_win_n}`);
                }).start();
            new TweenJS.Tween(this.text)
                .to({ x: this.endPosition.x, y: this.endPosition.y }, 300)
                .onComplete(() => { this.showGlowElements(); })
                .start();
        });
    }
}
